import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useCreateProduct, useUpdateProduct } from '@hooks/useProcurement'

const productSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  category: z.string().optional().or(z.literal('')),
  hsnCode: z.string().regex(/^\d{4,8}$/, 'HSN code is required (4 to 8 digits)'),
  baseUom: z.string().min(1, 'UOM is required'),
  productType: z.enum(['REPACK', 'DIRECT_SALE']),
  expectedLossPct: z.coerce.number().min(0).max(100).default(0),
  shelfLifeDays: z.coerce.number().int().positive().optional(),
  nearExpiryAlertDays: z.coerce.number().int().default(30),
  stagnantAlertDays: z.coerce.number().int().default(45),
})

type ProductFormData = z.infer<typeof productSchema>

interface ProductFormProps {
  initialData?: any
  onSuccess?: () => void
  onCancel?: () => void
}

export function ProductForm({
  initialData,
  onSuccess,
  onCancel,
}: ProductFormProps) {
  const [error, setError] = useState<string | null>(null)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProductFormData>({
    defaultValues: initialData || {
      name: '',
      category: '',
      hsnCode: '',
      baseUom: 'KG',
      productType: 'REPACK',
      expectedLossPct: 0,
      shelfLifeDays: undefined,
      nearExpiryAlertDays: 30,
      stagnantAlertDays: 45,
    },
  })

  const create = useCreateProduct()
  const update = useUpdateProduct(initialData?.id)

  const onSubmit = async (data: ProductFormData) => {
    try {
      setError(null)
      const cleanData = {
        ...data,
        category: data.category || null,
        hsnCode: data.hsnCode || null,
        shelfLifeDays: data.shelfLifeDays || null,
      }

      if (initialData?.id) {
        await update.mutateAsync(cleanData)
      } else {
        await create.mutateAsync(cleanData)
      }

      onSuccess?.()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save product')
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product Name *
          </label>
          <input
            {...register('name')}
            type="text"
            placeholder="e.g., Cardamom (Elaichi)"
            className="w-full"
          />
          {errors.name && (
            <span className="text-red-500 text-sm">{errors.name.message}</span>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <input
            {...register('category')}
            type="text"
            placeholder="e.g., Spices"
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            HSN Code
          </label>
          <input
            {...register('hsnCode')}
            type="text"
            placeholder="e.g., 0908"
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Base UOM *
          </label>
          <input
            {...register('baseUom')}
            type="text"
            placeholder="e.g., KG, PACKET"
            className="w-full"
          />
          {errors.baseUom && (
            <span className="text-red-500 text-sm">{errors.baseUom.message}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product Type *
          </label>
          <select {...register('productType')} className="w-full">
            <option value="REPACK">Repack (Bulk → Packets)</option>
            <option value="DIRECT_SALE">Direct Sale (Already Packaged)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Expected Loss % (default: 0%)
          </label>
          <input
            {...register('expectedLossPct', { valueAsNumber: true })}
            type="number"
            min="0"
            max="100"
            step="0.1"
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Shelf Life (days)
          </label>
          <input
            {...register('shelfLifeDays', { valueAsNumber: true })}
            type="number"
            min="1"
            placeholder="e.g., 365"
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Near Expiry Alert (days)
          </label>
          <input
            {...register('nearExpiryAlertDays', { valueAsNumber: true })}
            type="number"
            min="1"
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stagnant Stock Alert (days)
          </label>
          <input
            {...register('stagnantAlertDays', { valueAsNumber: true })}
            type="number"
            min="1"
            className="w-full"
          />
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save Product'}
        </button>
      </div>
    </form>
  )
}
